"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

const DEFAULT_EVENTS = ["pointerdown"];

// Calls `handler(event)` when a press lands outside the element(s). Replaces chawk-bazar/ciseco `useClickOutside`,
// the isource/mahya/tsco/shakhe-ara `OutsideElementClick` hook and the inline `handleClickOutside` copies.
// The latest `handler` and `refs` are always used (the old component kept the first `fn`: its effect deps were `[ref]`).
//
// refs: one ref or an array of refs (e.g. a sidebar and the button that opens it).
// options:
// - events: event names, default ["pointerdown"] (one event for mouse, touch and pen; mousedown alone misses taps on iOS)
// - capture: listen in the capture phase (fires even if something inside stops propagation). Default false.
// - enabled: false removes the listeners. Default true.
// - skip_unmounted: false (default) = nothing fires while any ref is empty, as every site copy did;
//   true = empty refs are ignored and it fires when the press is outside all mounted ones.
export default function useClickOutside(refs, handler, options = {}) {
	const { events = DEFAULT_EVENTS, capture = false, enabled = true, skip_unmounted = false } = options;

	const handler_ref = useRef(handler);
	const refs_ref = useRef(refs);
	useLayoutEffect(() => {
		handler_ref.current = handler;
		refs_ref.current = refs;
	});

	const events_key = (Array.isArray(events) ? events : [events]).filter(Boolean).join(" ");

	useEffect(() => {
		if (!enabled || !events_key) return;
		const names = events_key.split(" ");

		const listener = (event) => {
			const current = refs_ref.current;
			const list = Array.isArray(current) ? current : [current];
			const target = event?.target || null;
			let mounted = 0;
			for (const ref of list) {
				const element = ref?.current;
				if (!element) {
					if (skip_unmounted) continue;
					return;
				}
				mounted += 1;
				if (element.contains(target)) return;
			}
			if (!mounted) return;
			handler_ref.current?.(event);
		};

		names.forEach((name) => document.addEventListener(name, listener, capture));
		return () => names.forEach((name) => document.removeEventListener(name, listener, capture));
	}, [events_key, capture, enabled, skip_unmounted]);
}
